import { CoreError, assertSafeInt } from './errors'

/** Money is integer paisa (1 rupee = 100 paisa). */
export type Paisa = number

export interface FormatMoneyOptions {
  /** Prefix such as 'Rs.'; omitted when empty. */
  symbol?: string
  /** Nepali/Indian digit grouping (12,34,567.00). Default true. */
  lakhGrouping?: boolean
  /** Show '.00' for whole amounts. Default true. */
  alwaysDecimals?: boolean
}

function groupDigits(digits: string, lakh: boolean): string {
  if (digits.length <= 3) return digits
  const last3 = digits.slice(-3)
  const rest = digits.slice(0, -3)
  const size = lakh ? 2 : 3
  const groups: string[] = []
  for (let end = rest.length; end > 0; end -= size) {
    groups.unshift(rest.slice(Math.max(0, end - size), end))
  }
  return `${groups.join(',')},${last3}`
}

/** 12345678 → "Rs. 1,23,456.78" */
export function formatMoney(paisa: Paisa, options: FormatMoneyOptions = {}): string {
  assertSafeInt(paisa, 'paisa')
  const { symbol = '', lakhGrouping = true, alwaysDecimals = true } = options
  const negative = paisa < 0
  const abs = Math.abs(paisa)
  const rupees = groupDigits(Math.floor(abs / 100).toString(), lakhGrouping)
  const fraction = (abs % 100).toString().padStart(2, '0')
  const amount = alwaysDecimals || fraction !== '00' ? `${rupees}.${fraction}` : rupees
  const signed = negative ? `-${amount}` : amount
  return symbol ? `${symbol} ${signed}` : signed
}

/**
 * Parses user input like "1,234.5", "Rs 1234", "-12.05" into paisa.
 * Rejects more than two decimal places instead of silently rounding.
 */
export function parseMoney(input: string): Paisa {
  const cleaned = input
    .trim()
    .replace(/^(rs\.?|npr|रु\.?)\s*/i, '')
    .replace(/[,\s]/g, '')
  const match = /^(-)?(\d*)(?:\.(\d{0,2}))?$/.exec(cleaned)
  if (!match || (match[2] === '' && (match[3] ?? '') === '')) {
    throw new CoreError('invalid_amount', `"${input}" is not an amount`)
  }
  const rupees = Number(match[2] || '0')
  const paisa = rupees * 100 + Number((match[3] ?? '').padEnd(2, '0'))
  assertSafeInt(paisa, 'amount')
  return match[1] ? -paisa : paisa
}

/** Splits `total` into integer parts proportional to `weights` (largest remainder; sums exactly). */
export function allocateProportionally(total: number, weights: readonly number[]): number[] {
  assertSafeInt(total, 'total')
  const weightSum = weights.reduce((a, w) => a + w, 0)
  if (weights.some((w) => w < 0 || !Number.isSafeInteger(w))) {
    throw new CoreError('invalid_input', 'weights must be non-negative integers')
  }
  if (weightSum === 0) {
    if (total === 0) return weights.map(() => 0)
    throw new CoreError('invalid_input', 'cannot allocate a non-zero total over zero weights')
  }
  const exact = weights.map((w) => BigInt(total) * BigInt(w))
  const base = exact.map((e) => Number(e / BigInt(weightSum)))
  let remainder = total - base.reduce((a, b) => a + b, 0)
  const order = exact
    .map((e, i) => ({ i, rem: Number(e % BigInt(weightSum)) }))
    .sort((a, b) => b.rem - a.rem || a.i - b.i)
  for (const { i } of order) {
    if (remainder === 0) break
    base[i] = (base[i] ?? 0) + 1
    remainder -= 1
  }
  return base
}

const ONES = [
  '',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
  'Ten',
  'Eleven',
  'Twelve',
  'Thirteen',
  'Fourteen',
  'Fifteen',
  'Sixteen',
  'Seventeen',
  'Eighteen',
  'Nineteen',
]
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']

function belowHundred(n: number): string {
  if (n < 20) return ONES[n] ?? ''
  const unit = n % 10
  return `${TENS[Math.floor(n / 10)]}${unit ? `-${ONES[unit]}` : ''}`
}

/** A whole number in words, counted the Nepali way: thousand, lakh, crore. */
function wholeInWords(n: number): string {
  if (n === 0) return 'Zero'
  const parts: string[] = []
  const crore = Math.floor(n / 1_00_00_000)
  const lakh = Math.floor(n / 1_00_000) % 100
  const thousand = Math.floor(n / 1000) % 100
  const hundred = Math.floor(n / 100) % 10
  const rest = n % 100
  if (crore) parts.push(`${wholeInWords(crore)} Crore`)
  if (lakh) parts.push(`${belowHundred(lakh)} Lakh`)
  if (thousand) parts.push(`${belowHundred(thousand)} Thousand`)
  if (hundred) parts.push(`${ONES[hundred]} Hundred`)
  if (rest) parts.push(belowHundred(rest))
  return parts.join(' ')
}

/**
 * The amount on a bill in words: 12345650 → "One Lakh Twenty-Three Thousand Four Hundred
 * Fifty-Six Rupees and Fifty Paisa only".
 */
export function amountInWords(paisa: Paisa): string {
  assertSafeInt(paisa, 'paisa')
  const abs = Math.abs(paisa)
  const rupees = Math.floor(abs / 100)
  const rest = abs % 100
  const words: string[] = []
  if (rupees || !rest) words.push(`${wholeInWords(rupees)} ${rupees === 1 ? 'Rupee' : 'Rupees'}`)
  if (rest) words.push(`${belowHundred(rest)} Paisa`)
  return `${paisa < 0 ? 'Minus ' : ''}${words.join(' and ')} only`
}
